import React, { useEffect, useMemo, useState } from "react";
import {
  Flex,
  Box,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Button,
  Text,
  Container,
  Heading,
  HStack,
  Tag,
  Input,
  InputGroup,
  InputLeftElement,
  Divider,
  useColorModeValue,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useSelector, useDispatch } from "react-redux";
import { fetchData } from "actions/actions";

// -----------------------------
// Helpers
// -----------------------------
const norm = (v) => (v ?? "").toString().trim().toLowerCase();

function normalizeType(t) {
  const x = norm(t);
  if (x.includes("journal")) return "journal";
  if (x.includes("conference")) return "conference";
  return x;
}

// -----------------------------
// Header (TRAVERSAL style)
// -----------------------------
function PageHeader() {
  const accent = "#06B6D4";
  const accent2 = "#22D3EE";

  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const titleColor = useColorModeValue("#0F172A", "white");
  const subColor = useColorModeValue("gray.700", "gray.300");

  const heroBg = useColorModeValue(
    "radial-gradient(1000px 420px at 12% 0%, rgba(6,182,212,0.18), transparent 60%), radial-gradient(900px 380px at 88% 25%, rgba(34,211,238,0.14), transparent 60%), linear-gradient(180deg, rgba(2,6,23,0.02), rgba(2,6,23,0.00))",
    "radial-gradient(1000px 420px at 12% 0%, rgba(6,182,212,0.20), transparent 60%), radial-gradient(900px 380px at 88% 25%, rgba(34,211,238,0.14), transparent 60%), linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.00))"
  );

  return (
    <Box pt={{ base: 7, md: 10 }} pb={{ base: 8, md: 10 }} bg={heroBg} borderBottom="1px solid" borderColor={border}>
      <Container maxW="1200px">
        <HStack spacing={2} flexWrap="wrap">
          <Tag
            size="lg"
            borderRadius="full"
            bg={useColorModeValue("white", "rgba(255,255,255,0.06)")}
            border="1px solid"
            borderColor={border}
            fontWeight="800"
          >
            University of Ottawa
          </Tag>

          <Tag
            size="lg"
            borderRadius="full"
            bg={useColorModeValue("rgba(6,182,212,0.12)", "rgba(6,182,212,0.18)")}
            color={useColorModeValue("#0E7490", "#67E8F9")}
            fontWeight="900"
          >
            TRAVERSAL
          </Tag>
        </HStack>

        <Heading mt={4} fontSize={{ base: "3xl", md: "4xl" }} color={titleColor} letterSpacing="tight" lineHeight={1.05}>
          <Box as="span" bgClip="text" bgGradient={`linear(to-r, ${accent}, ${accent2})`}>
            TRAVERSAL
          </Box>{" "}
          Publications
        </Heading>

        <Text mt={3} color={subColor} fontSize={{ base: "md", md: "lg" }} maxW="90ch" lineHeight={1.8}>
          Peer-reviewed outputs from the TRAVERSAL team, including journals and conferences.
        </Text>
      </Container>
    </Box>
  );
}

// -----------------------------
// Main Component
// -----------------------------
export default function PublicationsList() {
  const isMobile = useSelector((state) => state.windowSize.isMobile);
  const dispatch = useDispatch();

  // keep your same selector path
  const data = useSelector((state) => state.data?.data?.publications);

  // search query
  const [query, setQuery] = useState("");

  // pagination
  const itemsPerPage = 5;
  const [currentPageJr, setCurrentPageJr] = useState(1);
  const [currentPageCn, setCurrentPageCn] = useState(1);

  // fetch once
  useEffect(() => {
    dispatch(fetchData());
  }, [dispatch]);

  // normalize + split (newest first like your reverse() logic)
  const { journals, conferences } = useMemo(() => {
    const arr = Array.isArray(data) ? data : [];

    const mapped = arr.map((p) => ({
      ...p,
      _type: normalizeType(p?.type),
    }));

    const j = mapped.filter((p) => p._type === "journal").slice().reverse();
    const c = mapped.filter((p) => p._type === "conference").slice().reverse();

    return { journals: j, conferences: c };
  }, [data]);

  // keyword filter (searches completeTitle + title)
  const q = norm(query);

  const journalsFiltered = useMemo(() => {
    if (!q) return journals;
    return journals.filter((p) => {
      const hay = norm(`${p?.completeTitle ?? ""} ${p?.title ?? ""}`);
      return hay.includes(q);
    });
  }, [journals, q]);

  const conferencesFiltered = useMemo(() => {
    if (!q) return conferences;
    return conferences.filter((p) => {
      const hay = norm(`${p?.completeTitle ?? ""} ${p?.title ?? ""}`);
      return hay.includes(q);
    });
  }, [conferences, q]);

  // reset pages when query changes / list changes
  useEffect(() => {
    setCurrentPageJr(1);
    setCurrentPageCn(1);
  }, [query, journalsFiltered.length, conferencesFiltered.length]);

  // page math
  const totalPagesJr = Math.max(1, Math.ceil(journalsFiltered.length / itemsPerPage));
  const totalPagesCn = Math.max(1, Math.ceil(conferencesFiltered.length / itemsPerPage));

  const currentJournals = journalsFiltered.slice(
    (currentPageJr - 1) * itemsPerPage,
    currentPageJr * itemsPerPage
  );

  const currentConferences = conferencesFiltered.slice(
    (currentPageCn - 1) * itemsPerPage,
    currentPageCn * itemsPerPage
  );

  const handleNextPageJr = () => setCurrentPageJr((p) => Math.min(totalPagesJr, p + 1));
  const handlePrevPageJr = () => setCurrentPageJr((p) => Math.max(1, p - 1));
  const handleNextPageCn = () => setCurrentPageCn((p) => Math.min(totalPagesCn, p + 1));
  const handlePrevPageCn = () => setCurrentPageCn((p) => Math.max(1, p - 1));

  // styles (matches your old layout but modern)
  const titleColor = useColorModeValue("#315369", "white");
  const border = useColorModeValue("rgba(0,0,0,0.10)", "rgba(255,255,255,0.12)");
  const panelBg = useColorModeValue("white", "rgba(255,255,255,0.03)");
  const muted = useColorModeValue("gray.600", "gray.300");

  const SectionTitle = ({ children }) => (
    <Flex justify="left" fontSize={{ base: 22, md: 26 }} fontWeight="900" color={titleColor} mt={8} mb={4}>
      {children}
    </Flex>
  );

  const Pagination = ({ page, total, onPrev, onNext, hide }) => {
    if (hide) return null;
    return (
      <Flex justifyContent="center" mt={4} alignItems="center" gap={3} flexWrap="wrap">
        <Button onClick={onPrev} isDisabled={page === 1}>
          Previous
        </Button>
        <Text fontWeight="700">
          Page {page} of {total}
        </Text>
        <Button onClick={onNext} isDisabled={page === total}>
          Next
        </Button>
      </Flex>
    );
  };

  // Loading state
  if (!Array.isArray(data)) {
    return (
      <Box>
        <PageHeader />
        <Flex justify="center" py={10}>
          Loading...
        </Flex>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader />

      <Box py={{ base: 8, md: 10 }}>
        <Container maxW="1200px">
          {/* Search box */}
          <Box
            border="1px solid"
            borderColor={border}
            bg={panelBg}
            borderRadius="18px"
            p={{ base: 4, md: 5 }}
            mb={6}
          >
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color={useColorModeValue("gray.500", "gray.400")} />
              </InputLeftElement>
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder='Search by keywords (e.g., "Kantarci", "microgrid", "doi", "2024")'
                borderRadius="14px"
              />
            </InputGroup>

            <HStack mt={3} spacing={3} flexWrap="wrap">
              <Tag borderRadius="full" fontWeight="900">
                Showing {journalsFiltered.length + conferencesFiltered.length} items
              </Tag>
              {query ? (
                <Button size="sm" variant="ghost" fontWeight="900" onClick={() => setQuery("")}>
                  Clear
                </Button>
              ) : null}
            </HStack>
          </Box>

          {/* JOURNALS */}
          <SectionTitle>JOURNALS</SectionTitle>

          <Box
            border="1px solid"
            borderColor={border}
            borderRadius="18px"
            bg={panelBg}
            overflow="hidden"
            p={{ base: 3, md: 4 }}              // ✅ padding so items don't touch edges
          >
            <Accordion allowMultiple width="100%" defaultIndex={[0]}>
              {currentJournals.map((publication, index) => (
                <AccordionItem
                  key={`jr-${index}`}
                  border="1px solid"             // ✅ card border instead of list divider
                  borderColor={border}
                  borderRadius="18px"
                  bg={useColorModeValue("white", "rgba(255,255,255,0.03)")}
                  mb={{ base: 3, md: 4 }}         // ✅ spacing between items
                  overflow="hidden"
                  boxShadow={useColorModeValue("0 10px 22px rgba(0,0,0,0.06)", "none")}
                >
                  <AccordionButton
                    py={{ base: 4, md: 5 }}
                    px={{ base: 4, md: 5 }}
                    _hover={{
                      bg: useColorModeValue("rgba(49,83,105,0.04)", "rgba(255,255,255,0.05)"),
                    }}
                  >
                    <Box flex="1" textAlign="left">
                      <Text
                        fontSize={{ base: 16, md: 20 }}
                        fontWeight="800"
                        color={titleColor}
                        lineHeight="1.25"
                      >
                        {publication.title}
                      </Text>
                    </Box>

                    <AccordionIcon />
                  </AccordionButton>

                  <AccordionPanel
                    px={{ base: 4, md: 5 }}
                    pb={{ base: 5, md: 6 }}
                    fontSize={{ base: 14, md: 16 }}
                    textAlign={isMobile ? "center" : "left"}
                  >
                    <Text color={muted} lineHeight="1.8">
                      {publication.completeTitle}
                    </Text>
                  </AccordionPanel>
                </AccordionItem>
              ))}

              {journalsFiltered.length === 0 ? (
                <Box p={6} textAlign="center">
                  <Text color={muted} fontWeight="700">
                    No journals match your search.
                  </Text>
                </Box>
              ) : null}
            </Accordion>
          </Box>


          <Pagination
            page={currentPageJr}
            total={totalPagesJr}
            onPrev={handlePrevPageJr}
            onNext={handleNextPageJr}
            hide={journalsFiltered.length <= itemsPerPage}
          />

          <Divider my={{ base: 10, md: 12 }} borderColor={border} />

          {/* CONFERENCES */}
          <SectionTitle>CONFERENCES</SectionTitle>
            
          <Box
            border="1px solid"
            borderColor={border}
            borderRadius="18px"
            bg={panelBg}
            overflow="hidden"
            p={{ base: 3, md: 4 }}              // ✅ padding so items don't touch edges
          >
            <Accordion allowMultiple width="100%" defaultIndex={[0]}>
              {currentConferences.map((publication, index) => (
                <AccordionItem
                  key={`jr-${index}`}
                  border="1px solid"             // ✅ card border instead of list divider
                  borderColor={border}
                  borderRadius="18px"
                  bg={useColorModeValue("white", "rgba(255,255,255,0.03)")}
                  mb={{ base: 3, md: 4 }}         // ✅ spacing between items
                  overflow="hidden"
                  boxShadow={useColorModeValue("0 10px 22px rgba(0,0,0,0.06)", "none")}
                >
                  <AccordionButton
                    py={{ base: 4, md: 5 }}
                    px={{ base: 4, md: 5 }}
                    _hover={{
                      bg: useColorModeValue("rgba(49,83,105,0.04)", "rgba(255,255,255,0.05)"),
                    }}
                  >
                    <Box flex="1" textAlign="left">
                      <Text
                        fontSize={{ base: 16, md: 20 }}
                        fontWeight="800"
                        color={titleColor}
                        lineHeight="1.25"
                      >
                        {publication.title}
                      </Text>
                    </Box>

                    <AccordionIcon />
                  </AccordionButton>

                  <AccordionPanel
                    px={{ base: 4, md: 5 }}
                    pb={{ base: 5, md: 6 }}
                    fontSize={{ base: 14, md: 16 }}
                    textAlign={isMobile ? "center" : "left"}
                  >
                    <Text color={muted} lineHeight="1.8">
                      {publication.completeTitle}
                    </Text>
                  </AccordionPanel>
                </AccordionItem>
              ))}

              {conferencesFiltered.length === 0 ? (
                <Box p={6} textAlign="center">
                  <Text color={muted} fontWeight="700">
                    No journals match your search.
                  </Text>
                </Box>
              ) : null}
            </Accordion>
          </Box>

          <Pagination
            page={currentPageCn}
            total={totalPagesCn}
            onPrev={handlePrevPageCn}
            onNext={handleNextPageCn}
            hide={conferencesFiltered.length <= itemsPerPage}
          />
        </Container>
      </Box>
    </Box>
  );
}
